import { apiRequest } from '@/utils/api-request';
import * as Sentry from '@sentry/react';
import type {
  RevokeResponse,
  ShareInviteResponse,
  ShareListResponse,
  WorkflowShareRole,
} from '@/types/share-types';

/** Request body for inviting users to a workflow */
export interface ShareWorkflowRequest {
  emails: string[];
  role?: WorkflowShareRole;
}

/**
 * Invite people to a workflow via email.
 */
export async function shareWorkflow(
  orgId: string,
  workflowId: string,
  body: ShareWorkflowRequest,
): Promise<ShareInviteResponse | { detail: string }> {
  try {
    const response = await apiRequest<ShareInviteResponse>({
      url: `/api/v1/orgs/${orgId}/workflows/${workflowId}/share`,
      target: 'workflow',
      method: 'POST',
      body: body as unknown as Record<string, unknown>,
    });

    if (response?.detail) {
      // i.e. 422 with shared_with for info about failed emails
      const errorBody = (response as { data?: { shared_with?: unknown } }).data;
      if (errorBody?.shared_with) {
        return errorBody as ShareInviteResponse;
      }
      return { detail: response.detail };
    }

    const data = response.data as unknown as ShareInviteResponse;
    return data;
  } catch (error) {
    console.error('Share workflow error:', error);
    Sentry.captureException(error, {
      extra: { context: 'share-workflow' },
    });
    throw error;
  }
}

/**
 * List people with access to a workflow.
 */
export async function listWorkflowAccess(
  orgId: string,
  workflowId: string,
): Promise<ShareListResponse> {
  try {
    const response = await apiRequest<ShareListResponse>({
      url: `/api/v1/orgs/${orgId}/workflows/${workflowId}/access`,
      target: 'workflow',
      method: 'GET',
    });

    if (response?.detail) {
      return { access: [] };
    }

    const data = response.data as unknown as ShareListResponse;
    return data;
  } catch (error) {
    console.error('listWorkflowAccess - Error:', error);
    Sentry.captureException(error, {
      extra: { context: 'list-workflow-access' },
    });
    return { access: [] };
  }
}

/**
 * Revoke a user's access to a workflow.
 */
export async function revokeWorkflowAccess(
  orgId: string,
  workflowId: string,
  email: string,
): Promise<RevokeResponse | { detail: string }> {
  try {
    const response = await apiRequest<RevokeResponse>({
      url: `/api/v1/orgs/${orgId}/workflows/${workflowId}/revoke`,
      target: 'workflow',
      method: 'POST',
      body: { email } as unknown as Record<string, unknown>,
    });

    if (response?.detail && !('status' in (response.data ?? {}))) {
      return { detail: response.detail };
    }

    return response.data as unknown as RevokeResponse;
  } catch (error) {
    console.error('Revoke workflow access error:', error);
    Sentry.captureException(error, {
      extra: { context: 'revoke-workflow-access' },
    });
    throw error;
  }
}
